import {Card, CardContent} from "@/components/ui/card";
import {Loader} from "@/src/modules/core/components";
import useResidentHouseholdId from "@/src/modules/core/hooks/use-resident-household-id";
import useResidentMembership from "@/src/modules/core/hooks/use-resident-membership";
import useHousehold from "@/src/modules/households/hooks/use-household";
import {
  getHouseholdOwnerMembership,
  isOwnerOccupantSamePerson,
} from "@/src/modules/households/lib/household-membership";
import {cn} from "@/lib/utils";

import {
  RESIDENT_HOME_BLOCK_TITLE_CLASS,
  RESIDENT_HOME_CARD_CONTENT_CLASS,
  RESIDENT_HOME_CARD_SHELL_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS,
  RESIDENT_HOME_TEXT_INSET_CLASS,
} from "../lib/resident-home-block-layout";
import {RESIDENT_HOME_LANDLORD_ICON} from "../lib/resident-home-teaser";
import {ResidentHomeIconTextBlock} from "./resident-home-icon-text-block";

type LandlordField = {
  label: string;
  value: string;
};

function LandlordFieldRow({label, value}: LandlordField) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground truncate text-sm">{value}</p>
    </div>
  );
}

export function ResidentHomeLandlordCard() {
  const householdId = useResidentHouseholdId();
  const membership = useResidentMembership();
  const {household, isPending} = useHousehold(householdId);

  const isTenant = Boolean(membership?.is_occupant && !membership.is_owner);

  if (!isTenant || !householdId) {
    return null;
  }

  const owner = getHouseholdOwnerMembership(household?.memberships);
  const ownerOccupantSame = isOwnerOccupantSamePerson(household?.memberships);

  if (!isPending && (!owner || ownerOccupantSame)) {
    return null;
  }

  const email = owner?.email?.trim() || null;
  const fields: LandlordField[] = owner
    ? [
        {label: "Name", value: owner.name?.trim() || "—"},
        ...(email ? [{label: "Email", value: email}] : []),
      ]
    : [];
  const fieldGridClass =
    fields.length >= 3 ? "sm:grid-cols-3" : fields.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1";

  return (
    <Card className={RESIDENT_HOME_CARD_SHELL_CLASS}>
      <CardContent className={cn(RESIDENT_HOME_CARD_CONTENT_CLASS, "flex flex-col gap-3 pb-5")}>
        <div className={RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS}>
          <ResidentHomeIconTextBlock
            icon={RESIDENT_HOME_LANDLORD_ICON.icon}
            iconWrapperClassName={RESIDENT_HOME_LANDLORD_ICON.iconWrapperClassName}
            iconClassName={RESIDENT_HOME_LANDLORD_ICON.iconClassName}
            className="min-w-0 flex-1 items-start"
            title={<p className={RESIDENT_HOME_BLOCK_TITLE_CLASS}>Landlord</p>}
          />
        </div>

        {isPending ? (
          <Loader className={cn(RESIDENT_HOME_TEXT_INSET_CLASS, "min-h-12")} />
        ) : (
          <div className={cn(RESIDENT_HOME_TEXT_INSET_CLASS, "grid grid-cols-1 gap-3", fieldGridClass)}>
            {fields.map((field) => (
              <LandlordFieldRow key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
